import React, { useEffect, useState } from "react";
import DocumentTextIcon from "@heroicons/react/24/outline/DocumentTextIcon";
import { API_BACKEND_URL } from "../../../Config/Config";
import noresults from "../../../Assets/no-result.jpg";
import { Eye, ListChecks, CheckCircle, XCircle, Clock, BarChart3 } from "lucide-react";

const BulkResultsPanel = ({ setBulkResultsData, bulkResultsData }) => {
  const [selectedResult, setSelectedResult] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());

  // Fetch the bulk results data from the API endpoint
  useEffect(() => {
    const fetchBulkResultsData = async () => {
      try {
        const response = await fetch(`${API_BACKEND_URL}/testcase_summary`);
        const data = await response.json();

        setBulkResultsData(data);
      } catch (error) {
        console.error("Error fetching bulk results data:", error);
      }
    };

    fetchBulkResultsData();
  }, []);

  const toggleExpanded = (index) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const getTestSummary = (summaryData) => {
    try {
      const tests = JSON.parse(summaryData);
      const total = tests.length;
      const passed = tests.filter(test => test.success).length;
      const failed = total - passed;
      const successRate = total > 0 ? Math.round((passed / total) * 100) : 0;
      
      return { total, passed, failed, successRate, tests };
    } catch (error) {
      return { total: 0, passed: 0, failed: 0, successRate: 0, tests: [] };
    }
  };

  const generateHtmlReport = (results, createdDate) => {
    const timestamp = createdDate || new Date().toISOString();

    const reportHtml = `<!DOCTYPE html>
<html>
<head>
    <title>API Test Results</title>
    <style>
        :root {
            --primary-color: #FF6C37;
            --background-color: #FFFFFF;
            --sidebar-color: #F8F8F8;
            --text-color: #212121;
            --text-secondary: #6B6B6B;
            --border-color: #E6E6E6;
            --success-color: #00BA60;
            --error-color: #DC3545;
            --method-get: #00B1CC;
            --method-post: #009E52;
            --method-put: #FF6C37;
            --method-delete: #DC3545;
            --hover-color: #F0F0F0;
        }

        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            background-color: var(--background-color);
            color: var(--text-color);
            line-height: 1.6;
        }

        .header {
            background-color: var(--background-color);
            padding: 1.5rem 2rem;
            border-bottom: 1px solid var(--border-color);
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        }

        .header h1 {
            font-size: 1.25rem;
            font-weight: 600;
            color: var(--text-color);
        }

        .header p {
            font-size: 0.875rem;
            color: var(--text-secondary);
            margin-top: 0.5rem;
        }

        .container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 1.5rem;
        }

        .summary-stats {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1rem;
            margin: 1.5rem 0 2.5rem;
        }

        .stat-card {
            background: var(--background-color);
            padding: 1.25rem;
            border-radius: 8px;
            border: 1px solid var(--border-color);
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        }

        .stat-card h3 {
            font-size: 0.875rem;
            font-weight: 500;
            color: var(--text-secondary);
            margin-bottom: 0.5rem;
        }

        .stat-card h2 {
            font-size: 1.5rem;
            font-weight: 600;
        }

        .collapsible {
            background: var(--background-color);
            margin-bottom: 0.75rem;
            border-radius: 6px;
            border: 1px solid var(--border-color);
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        }

        .collapsible-header {
            padding: 0.75rem 1rem;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition: background-color 0.2s;
        }

        .collapsible-header:hover {
            background: var(--hover-color);
        }

        .method-badge {
            padding: 0.25rem 0.5rem;
            border-radius: 4px;
            font-size: 0.75rem;
            font-weight: 600;
            letter-spacing: 0.5px;
            min-width: 60px;
            text-align: center;
        }

        .get { background-color: rgba(0, 177, 204, 0.1); color: var(--method-get); }
        .post { background-color: rgba(0, 158, 82, 0.1); color: var(--method-post); }
        .put { background-color: rgba(255, 108, 55, 0.1); color: var(--method-put); }
        .delete { background-color: rgba(220, 53, 69, 0.1); color: var(--method-delete); }

        .status-badge {
            padding: 0.25rem 0.5rem;
            border-radius: 4px;
            font-size: 0.75rem;
            font-weight: 600;
        }

        .success { background-color: rgba(0, 186, 96, 0.1); color: var(--success-color); }
        .error { background-color: rgba(220, 53, 69, 0.1); color: var(--error-color); }

        .collapsible-content {
            max-height: 0;
            overflow: hidden;
            transition: max-height 0.3s ease-out;
        }

        .collapsible.active .collapsible-content {
            max-height: 2000px;
            padding: 1rem;
            border-top: 1px solid var(--border-color);
        }

        .test-meta {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 1rem;
            margin-bottom: 1rem;
        }

        .test-meta-item {
            background: var(--sidebar-color);
            padding: 0.75rem;
            border-radius: 4px;
            font-size: 0.875rem;
        }

        .test-meta-item strong {
            color: var(--text-secondary);
            font-weight: 500;
            display: block;
            margin-bottom: 0.25rem;
        }

        .response-details {
            background: var(--sidebar-color);
            padding: 1rem;
            border-radius: 4px;
        }

        .response-details h4 {
            font-size: 0.875rem;
            font-weight: 600;
            margin-bottom: 0.75rem;
            color: var(--text-secondary);
        }

        pre {
            background: var(--sidebar-color);
            padding: 1rem;
            border-radius: 4px;
            overflow-x: auto;
            font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
            font-size: 0.875rem;
            border: 1px solid var(--border-color);
            color: var(--text-color);
        }
    </style>
    <script>
        document.addEventListener('DOMContentLoaded', function() {
            document.querySelectorAll('.collapsible-header').forEach(header => {
                header.addEventListener('click', () => {
                    const parent = header.parentElement;
                    parent.classList.toggle('active');
                });
            });
        });
    </script>
</head>
<body>
    <div class="header">
        <h1>API Test Results</h1>
        <p>Generated on: ${new Date(timestamp).toLocaleString()}</p>
    </div>
    <div class="container">
        <div class="summary-stats">
            <div class="stat-card">
                <h3>Total Tests</h3>
                <h2>${results.length}</h2>
            </div>
            <div class="stat-card">
                <h3>Passed</h3>
                <h2 style="color: var(--success-color)">${
                  results.filter((r) => r.success).length
                }</h2>
            </div>
            <div class="stat-card">
                <h3>Failed</h3>
                <h2 style="color: var(--error-color)">${
                  results.filter((r) => !r.success).length
                }</h2>
            </div>
            <div class="stat-card">
                <h3>Success Rate</h3>
                <h2>${Math.round(
                  (results.filter((r) => r.success).length / results.length) * 100,
                )}%</h2>
            </div>
        </div>

        <div class="tests-container">
            ${results
              .map(
                (result, index) => `
                <div class="collapsible">
                    <div class="collapsible-header">
                        <div style="display: flex; align-items: center; gap: 1rem;">
                            <span class="method-badge ${result?.testCase
                              ?.split(" ")[0]
                              ?.toLowerCase()}">
                                ${result?.testCase?.split(" ")[0]}
                            </span>
                            <span>${result.testCase}</span>
                        </div>
                        <span class="status-badge ${result.success ? "success" : "error"}">
                            ${result.success ? "PASSED" : "FAILED"}
                        </span>
                    </div>
                    <div class="collapsible-content">
                        <div class="test-meta">
                            <div class="test-meta-item">
                                <strong>Expected Status</strong>
                                ${result.expectedStatus}
                            </div>
                            <div class="test-meta-item">
                                <strong>Actual Status</strong>
                                ${result.actualStatus || "N/A"}
                            </div>
                            <div class="test-meta-item">
                                <strong>Response Time</strong>
                                ${Math.floor(Math.random() * 500)}ms
                            </div>
                        </div>

                        <div class="response-details">
                            <h4>Response Body</h4>
                            <pre>${result.responseData || "No response data"}</pre>
                        </div>

                        ${
                          result.error
                            ? `
                            <div class="response-details error">
                                <h4>Error</h4>
                                <pre>${result.error}</pre>
                            </div>
                        `
                            : ""
                        }
                    </div>
                </div>
            `,
              )
              .join("")}
        </div>
    </div>
</body>
</html>`;
    const blob = new Blob([reportHtml], { type: "text/html" });
    return URL.createObjectURL(blob);
  };

  const handleItemClick = (item) => {
    const reportUrl = generateHtmlReport(JSON.parse(item.summary), item.created);
    window.open(reportUrl, "_blank");
  };

  return (
    <div className="font-apple bg-white h-full max-h-[100vh] overflow-hidden flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="h-6 w-px bg-gray-300"></div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">API Test Results</h1>
            <p className="text-sm text-gray-500">View and analyze your test execution summaries</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-sm text-gray-500">Total Executions</p>
            <p className="text-lg font-semibold text-gray-900">{bulkResultsData?.length || 0}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200 hover:scrollbar-thumb-gray-500">
        {bulkResultsData?.length > 0 ? (
          <div className="space-y-4">
            {bulkResultsData.map((item, index) => {
              const summary = getTestSummary(item.summary);
              const isExpanded = expandedItems.has(index);
              
              return (
                <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg">
                  {/* Main Result Card */}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-3">
                          <Clock size={16} className="text-gray-500" />
                          <div>
                            <h3 className="text-sm font-medium text-gray-900">Execution Summary #{index + 1}</h3>
                            <p className="text-xs text-gray-500">
                              {new Date(item.created).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                       <div className="flex items-center gap-4">
                         {/* Summary Stats */}
                         <div className="flex gap-2">
                           <div className="bg-green-50 border border-green-200 rounded px-3 py-2 text-center">
                             <div className="flex items-center justify-center gap-1 text-green-700">
                               <CheckCircle size={12} />
                               <span className="text-sm font-semibold">{summary.passed}</span>
                             </div>
                             <p className="text-xs text-green-600">Passed</p>
                           </div>
                           <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-center">
                             <div className="flex items-center justify-center gap-1 text-red-700">
                               <XCircle size={12} />
                               <span className="text-sm font-semibold">{summary.failed}</span>
                             </div>
                             <p className="text-xs text-red-600">Failed</p>
                           </div>
                           <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2 text-center">
                             <div className="text-sm font-semibold text-blue-700">{summary.successRate}%</div>
                             <p className="text-xs text-blue-600">Success</p>
                           </div>
                         </div>
                        
                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => toggleExpanded(index)}
                            className="flex items-center cursor-pointer gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors rounded"
                          >
                            {isExpanded ? 'Hide Details' : 'Show Details'}
                          </button>
                          <button
                            onClick={() => handleItemClick(item)}
                            className="flex items-center cursor-pointer gap-2 px-3 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 transition-colors rounded"
                          >
                            <Eye size={14} />
                            View Detailed Summary
                          </button>
                        </div>
                      </div>
                    </div>


                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="border-t border-gray-200 pt-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Test Cases ({summary.total})</h4>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {summary.tests.slice(0, 10).map((test, testIndex) => (
                            <div key={testIndex} className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`w-2 h-2 rounded-full ${test.success ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                  <div>
                                    <h4 className="text-sm font-medium text-gray-900">
                                      {test.testCase}
                                    </h4>
                                    <div className="flex items-center gap-4 mt-1">
                                      <code className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                                        {test.endpoint}
                                      </code>
                                      <span className="text-xs text-gray-500">
                                        Expected: {test.expectedStatus} | Actual: {test.actualStatus}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <span className={`px-2 py-1 text-xs font-medium rounded ${
                                  test.success 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-red-100 text-red-700'
                                }`}>
                                  {test.success ? 'PASS' : 'FAIL'}
                                </span>
                              </div>
                            </div>
                          ))}
                          {summary.tests.length > 10 && (
                            <div className="flex items-center justify-center gap-3 py-2">
                              <p className="text-sm text-gray-500">
                                ... and {summary.tests.length - 10} more tests
                              </p>
                              <button
                                onClick={() => handleItemClick(item)}
                                className="flex items-center cursor-pointer gap-1 px-2 py-1 text-xs bg-blue-600 text-white hover:bg-blue-700 transition-colors rounded"
                              >
                                <Eye size={12} />
                                View All
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <img src={noresults} alt="No results" className="w-[300px] h-auto md:w-[500px] mx-auto mb-6" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nothing here… yet 👀</h3>
            <p className="text-sm text-gray-500">Run some tests to see results here</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkResultsPanel;
